import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { CheckCircle } from "@material-ui/icons";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAGS") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchInput: {
    backgroundColor: "#f5f5f7",
    borderRadius: 30,
    '& .MuiOutlinedInput-root': {
      borderRadius: 30,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: theme.spacing(1),
    },
  },
  headerButtonsWrapper: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      width: '100%',
    },
  },
  actionButton: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  cardGrid: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
    },
  },
  card: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    marginTop: theme.spacing(0.5),
  },
  cardBase: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
    border: "1px solid rgba(148, 163, 184, 0.3)",
  },
  tagChip: {
    backgroundColor: "transparent",
    borderRadius: 999,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  ticketsText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const Tags = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 1 },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
  }, [socket]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = async () => {
    setSelectedTag(null);
    setTagModalOpen(false);
    // Recarrega a lista para garantir que edições apareçam imediatamente
    try {
      const { data } = await api.get("/tags/", {
        params: { searchParam, pageNumber, kanban: 1 },
      });
      dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_TAGS", payload: data.tags });
    } catch (err) { /* silencioso */ }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTag && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}
      <MainHeader>
        <div>
          <Title>{i18n.t("tagsKanban.title")} ({tags.length})</Title>
          <Typography variant="body2" className={classes.pageSubtitle}>
            Gerencie os quadros que organizam os contatos no Kanban.
          </Typography>
        </div>
        <MainHeaderButtonsWrapper className={classes.headerButtonsWrapper}>
          <TextField
            placeholder="Buscar quadros do Kanban..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className={classes.actionButton}
            style={{
              color: "white",
              backgroundColor: "#437db5",
              boxShadow: "none",
              borderRadius: "5px",
            }}
            onClick={handleOpenTagModal}
          >
            {i18n.t("tagsKanban.buttons.add")}
          </Button>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            className={classes.actionButton}
            style={{
              color: "white",
              backgroundColor: "#4ec24e",
              boxShadow: "none",
              borderRadius: "5px",
            }}
            onClick={handleReturnToKanban}
          >
            {'Voltar para o Kanban'}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Grid container spacing={2} className={classes.cardGrid}>
          {loading ? (
            <Grid item xs={12}>
              <Card variant="outlined" 
                className={classes.cardBase}
              >
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Carregando...
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            tags.map((tag) => (
              <Grid item xs={12} sm={6} md={4} key={tag.id} className={classes.card}>
                <Card
                  variant="outlined"
                  className={classes.cardBase}
                >
                  <CardContent>
                    <Chip
                      variant="default"
                      className={classes.tagChip}
                      style={{
                        backgroundColor: tag.color,
                        color: "#fff",
                        marginBottom: 10,
                        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.3)",
                      }}
                      label={tag.name}
                      size="small"
                    />
                    <Typography variant="body2" align="center" className={classes.ticketsText}>
                      Tickets: {tag?.ticketTags?.length || 0}
                    </Typography>
                  </CardContent>
                  <CardActions style={{ 
                    justifyContent: "center", 
                    gap: "10px",
                    flexWrap: isMobile ? 'wrap' : 'nowrap'
                  }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditTag(tag)}
                      style={{
                        backgroundColor: "#40BFFF",
                        padding: "8px",
                        borderRadius: "10px",
                        minWidth: isMobile ? '100%' : 'auto',
                      }}
                      title="Editar Tag"
                    >
                      <EditIcon style={{ color: "#fff" }} />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingTag(tag);
                      }}
                      style={{
                        backgroundColor: "#FF6B6B",
                        padding: "8px",
                        borderRadius: "10px",
                        minWidth: isMobile ? '100%' : 'auto',
                      }}
                      title="Excluir Tag"
                    >
                      <DeleteOutlineIcon style={{ color: "#fff" }} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default Tags;